import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  AlertTriangle, 
  Activity, 
  Brain, 
  Frown,
  Zap,
  ThermometerSun,
  HeartPulse,
  Utensils,
  Info
} from 'lucide-react';
import PsychoeducationTooltip from '@/components/education/PsychoeducationTooltip';

interface PhysicalWellbeingData {
  stress_level?: number;
  anxiety_level?: number;
  mood_score?: number;
  fatigue_level?: number;
  pain_level?: number;
  pain_locations?: string[];
  headache_severity?: number;
  muscle_tension?: number;
  digestive_comfort?: number;
  appetite_level?: string;
  physical_symptoms?: string[];
}

interface PhysicalWellbeingCheckInProps {
  initialData?: PhysicalWellbeingData;
  onDataChange: (data: PhysicalWellbeingData) => void;
}

const PAIN_LOCATIONS = [
  'Head', 'Neck', 'Shoulders', 'Upper Back', 'Lower Back',
  'Chest', 'Abdomen', 'Hips', 'Knees', 'Feet', 'Hands', 'Jaw'
];

const PHYSICAL_SYMPTOMS = [
  // Pain & Discomfort
  'Headache', 'Chronic pain', 'Back pain', 'Neck pain', 'Muscle aches', 'Joint stiffness',
  // Cardiovascular
  'Heart palpitations', 'Chest tightness', 'Shortness of breath',
  // Neurological
  'Dizziness', 'Brain fog', 'Numbness/tingling', 'Tinnitus', 'Trembling',
  // Temperature & Hormonal
  'Hot flushes', 'Night sweats', 'Cold extremities', 'Sweating',
  // Digestive
  'Nausea', 'Digestive issues', 'Bloating', 'Loss of appetite',
  // General
  'Fatigue', 'Weakness', 'Blurred vision', 'Dry mouth', 'Skin irritation'
];

const APPETITE_OPTIONS = [
  { value: 'increased', label: 'Increased appetite' },
  { value: 'normal', label: 'Normal appetite' },
  { value: 'decreased', label: 'Decreased appetite' },
  { value: 'no_appetite', label: 'No appetite' },
  { value: 'emotional_eating', label: 'Emotional eating' }
];

export default function PhysicalWellbeingCheckIn({ 
  initialData = {}, 
  onDataChange 
}: PhysicalWellbeingCheckInProps) {
  const [formData, setFormData] = useState<PhysicalWellbeingData>({
    stress_level: 5,
    anxiety_level: 0,
    mood_score: 5,
    fatigue_level: 0,
    pain_level: 0,
    pain_locations: [],
    headache_severity: 0,
    muscle_tension: 0,
    digestive_comfort: 5,
    appetite_level: 'normal',
    physical_symptoms: [],
    ...initialData
  });

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  const handleSliderChange = (field: keyof PhysicalWellbeingData, value: number[]) => {
    const newData = { ...formData, [field]: value[0] };
    setFormData(newData);
    onDataChange(newData);
  };

  const togglePainLocation = (location: string) => {
    const current = formData.pain_locations || [];
    const updated = current.includes(location)
      ? current.filter(l => l !== location)
      : [...current, location];
    const newData = { ...formData, pain_locations: updated };
    setFormData(newData);
    onDataChange(newData);
  };

  const toggleSymptom = (symptom: string) => {
    const current = formData.physical_symptoms || [];
    const updated = current.includes(symptom)
      ? current.filter(s => s !== symptom)
      : [...current, symptom];
    const newData = { ...formData, physical_symptoms: updated };
    setFormData(newData);
    onDataChange(newData);
  };

  const handleSelectChange = (field: keyof PhysicalWellbeingData, value: string) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    onDataChange(newData);
  };

  const getScoreColor = (score: number, inverse: boolean = false) => {
    if (inverse) {
      if (score <= 3) return 'text-primary';
      if (score <= 6) return 'text-accent';
      return 'text-destructive';
    }
    if (score >= 7) return 'text-primary';
    if (score >= 4) return 'text-accent';
    return 'text-destructive';
  };

  const getStressLabel = (level: number) => {
    if (level <= 2) return 'Very Low';
    if (level <= 4) return 'Low';
    if (level <= 6) return 'Moderate';
    if (level <= 8) return 'High';
    return 'Very High';
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold mb-2">🩺 Physical & Mental Wellbeing</h3>
        <p className="text-muted-foreground">
          Track stress, pain, fatigue, and other key indicators of your mind-body health
        </p>
      </div>

      {/* Stress Level - Primary Metric */}
      <Card className="p-6 border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-primary/20">
              <AlertTriangle className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h4 className="text-lg font-bold">Stress Level</h4>
              <p className="text-sm text-muted-foreground">How stressed do you feel right now?</p>
            </div>
          </div>
          <PsychoeducationTooltip 
            content="Chronic stress elevates cortisol, contributing to inflammation, poor sleep, weight gain, and mental health issues. Tracking stress helps identify patterns and triggers." 
          />
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Calm</span>
            <span className={`text-3xl font-bold ${getScoreColor(formData.stress_level || 5, true)}`}>
              {formData.stress_level || 5}/10
            </span>
            <span className="text-sm text-muted-foreground">Overwhelmed</span>
          </div>
          <Slider
            value={[formData.stress_level || 5]}
            onValueChange={(value) => handleSliderChange('stress_level', value)}
            min={1}
            max={10}
            step={1}
            className="w-full"
          />
          <div className="text-center">
            <Badge variant={
              (formData.stress_level || 5) <= 3 ? 'default' :
              (formData.stress_level || 5) <= 6 ? 'secondary' : 'destructive'
            }>
              {getStressLabel(formData.stress_level || 5)}
            </Badge>
          </div>
        </div>
      </Card>

      {/* Mood & Anxiety Row */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Mood Score */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-full bg-primary/10">
              <Frown className="w-5 h-5 text-primary" />
            </div>
            <Label className="text-base font-semibold">Mood</Label>
            <span className={`ml-auto text-2xl font-bold ${getScoreColor(formData.mood_score || 5)}`}>
              {formData.mood_score || 5}
            </span>
          </div>
          <div className="px-2">
            <Slider
              value={[formData.mood_score || 5]}
              onValueChange={(value) => handleSliderChange('mood_score', value)}
              min={1}
              max={10}
              step={1}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-3">
              <span>Very Low</span>
              <span>Excellent</span>
            </div>
          </div>
        </Card>

        {/* Anxiety Level */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-full bg-accent/10">
              <Brain className="w-5 h-5 text-accent" />
            </div>
            <Label className="text-base font-semibold">Anxiety</Label>
            <span className={`ml-auto text-2xl font-bold ${getScoreColor(formData.anxiety_level || 0, true)}`}>
              {formData.anxiety_level || 0}
            </span>
          </div>
          <div className="px-2">
            <Slider
              value={[formData.anxiety_level || 0]}
              onValueChange={(value) => handleSliderChange('anxiety_level', value)}
              min={0}
              max={10}
              step={1}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-3">
              <span>None</span>
              <span>Severe</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Fatigue & Energy Balance */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-full bg-accent/10">
            <Zap className="w-5 h-5 text-accent" />
          </div>
          <Label className="text-base font-semibold">Fatigue Level</Label>
          <PsychoeducationTooltip 
            content="Fatigue is distinct from low energy - it represents exhaustion that rest doesn't easily fix. Persistent fatigue can indicate burnout, depression, or physical health issues." 
          />
          <span className={`ml-auto text-2xl font-bold ${getScoreColor(formData.fatigue_level || 0, true)}`}>
            {formData.fatigue_level || 0}
          </span>
        </div>
        <div className="px-2">
          <Slider
            value={[formData.fatigue_level || 0]}
            onValueChange={(value) => handleSliderChange('fatigue_level', value)}
            min={0}
            max={10}
            step={1}
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-3">
            <span>Fully Rested</span>
            <span>Exhausted</span>
          </div>
        </div>
      </Card>

      {/* Pain Section */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-full bg-destructive/10">
            <ThermometerSun className="w-5 h-5 text-destructive" />
          </div>
          <Label className="text-base font-semibold">Pain Level</Label>
          <PsychoeducationTooltip 
            content="Pain is a complex experience influenced by both physical and psychological factors. Chronic pain correlates strongly with stress, sleep quality, and mental health." 
          />
          <span className={`ml-auto text-2xl font-bold ${getScoreColor(formData.pain_level || 0, true)}`}>
            {formData.pain_level || 0}
          </span>
        </div>
        <div className="px-2">
          <Slider
            value={[formData.pain_level || 0]}
            onValueChange={(value) => handleSliderChange('pain_level', value)}
            min={0}
            max={10}
            step={1}
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-3">
            <span>No Pain</span>
            <span>Severe Pain</span>
          </div>
        </div>

        {(formData.pain_level || 0) > 0 && (
          <div className="mt-5 pt-4 border-t border-border/50">
            <Label className="text-sm font-medium mb-3 block">Where is the pain?</Label>
            <div className="flex flex-wrap gap-2 px-1">
              {PAIN_LOCATIONS.map((location) => (
                <Badge
                  key={location}
                  variant={(formData.pain_locations || []).includes(location) ? 'default' : 'outline'}
                  className="cursor-pointer transition-all hover:scale-105 px-3 py-1"
                  onClick={() => togglePainLocation(location)}
                >
                  {location}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Muscle Tension & Headache Row */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-full bg-primary/10">
              <Activity className="w-5 h-5 text-primary" />
            </div>
            <Label className="text-base font-semibold">Muscle Tension</Label>
            <span className={`ml-auto text-2xl font-bold ${getScoreColor(formData.muscle_tension || 0, true)}`}>
              {formData.muscle_tension || 0}
            </span>
          </div>
          <div className="px-2">
            <Slider
              value={[formData.muscle_tension || 0]}
              onValueChange={(value) => handleSliderChange('muscle_tension', value)}
              min={0}
              max={10}
              step={1}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-3">
              <span>Relaxed</span>
              <span>Very Tense</span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-full bg-accent/10">
              <Brain className="w-5 h-5 text-accent" />
            </div>
            <Label className="text-base font-semibold">Headache</Label>
            <span className={`ml-auto text-2xl font-bold ${getScoreColor(formData.headache_severity || 0, true)}`}>
              {formData.headache_severity || 0}
            </span>
          </div>
          <div className="px-2">
            <Slider
              value={[formData.headache_severity || 0]}
              onValueChange={(value) => handleSliderChange('headache_severity', value)}
              min={0}
              max={10}
              step={1}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-3">
              <span>None</span>
              <span>Severe</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Gut Health & Appetite */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-full bg-primary/10">
              <HeartPulse className="w-5 h-5 text-primary" />
            </div>
            <Label className="text-base font-semibold">Digestive Comfort</Label>
            <PsychoeducationTooltip 
              content="The gut-brain axis is a two-way communication system. Gut health directly impacts mood, anxiety, and cognitive function. Stress can cause digestive issues, and vice versa." 
            />
            <span className={`ml-auto text-2xl font-bold ${getScoreColor(formData.digestive_comfort || 5)}`}>
              {formData.digestive_comfort || 5}
            </span>
          </div>
          <div className="px-2">
            <Slider
              value={[formData.digestive_comfort || 5]}
              onValueChange={(value) => handleSliderChange('digestive_comfort', value)}
              min={1}
              max={10}
              step={1}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-3">
              <span>Very Uncomfortable</span>
              <span>Great</span>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-full bg-accent/10">
              <Utensils className="w-5 h-5 text-accent" />
            </div>
            <Label className="text-base font-semibold">Appetite</Label>
          </div>
          <div className="px-1">
            <Select 
              value={formData.appetite_level || 'normal'}
              onValueChange={(value) => handleSelectChange('appetite_level', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select appetite..." />
              </SelectTrigger>
              <SelectContent>
                {APPETITE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>
      </div>

      {/* Physical Symptoms Checklist */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-full bg-primary/10">
            <Info className="w-5 h-5 text-primary" />
          </div>
          <Label className="text-base font-semibold">Physical Symptoms Today</Label>
          <PsychoeducationTooltip 
            content="Somatic symptoms are physical manifestations of psychological states. Many people experience anxiety or depression primarily through physical symptoms like racing heart, sweating, or muscle tension." 
          />
        </div>
        <p className="text-sm text-muted-foreground mb-4 px-1">Select any symptoms you're experiencing:</p>
        <div className="flex flex-wrap gap-2 px-1">
          {PHYSICAL_SYMPTOMS.map((symptom) => (
            <Badge
              key={symptom}
              variant={(formData.physical_symptoms || []).includes(symptom) ? 'default' : 'outline'}
              className="cursor-pointer transition-all hover:scale-105 px-3 py-1.5"
              onClick={() => toggleSymptom(symptom)}
            >
              {symptom}
            </Badge>
          ))}
        </div>
      </Card>

      {/* Clinical Insight */}
      <Card className="p-4 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-primary mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Why This Matters</p>
            <p>
              Research shows strong correlations between stress and physical symptoms. High stress increases inflammation markers by up to 60%, 
              affects sleep quality, and can manifest as pain, fatigue, or digestive issues. Tracking these patterns helps identify 
              what's driving your symptoms and when to seek support.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
